import React from 'react'
import { Input, Pagination, Skeleton } from '@/shared/ui'
import { AlertModal } from '@/shared/ui/AlertModal'
import { useToast } from '@/shared/ui/Toast'
import { useAffiliateLeads } from '../hooks/useAffiliateLeads'
import AffiliateLeadsTable from './AffiliateLeadsTable'
import SimpleFeedbackModal from './SimpleFeedbackModal'
import AffiliateLeadEditModal from './AffiliateLeadEditModal'
import type { AffiliateLead } from '../types'
import { deleteAffiliateLead } from '../services/affiliateLeadService'

export default function AffiliateLeadsPanel() {
  const { add: toast } = useToast()
  const { items, total, page, setPage, pageSize, search, setSearch, loading, error, load } = useAffiliateLeads()

  const [current, setCurrent] = React.useState<AffiliateLead | null>(null)
  const [notesOpen, setNotesOpen] = React.useState(false)
  const [editOpen, setEditOpen] = React.useState(false)
  const [confirmDeleteOpen, setConfirmDeleteOpen] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)

  const totalPages = Math.max(1, Math.ceil(total / pageSize))

  function handleViewNotes(lead: AffiliateLead) {
    setCurrent(lead)
    setNotesOpen(true)
  }

  function handleEdit(lead: AffiliateLead) {
    setCurrent(lead)
    setEditOpen(true)
  }

  function handleDeleteRequest(lead: AffiliateLead) {
    setCurrent(lead)
    setConfirmDeleteOpen(true)
  }

  async function handleConfirmDelete() {
    if (!current || deleting) return
    setDeleting(true)
    try {
      await deleteAffiliateLead(current.id)
      await load()
      toast({
        title: 'Interessado excluído',
        variant: 'success',
        soft: true,
      })
    } catch (e: any) {
      toast({
        title: 'Falha ao excluir interessado',
        description: e?.message ?? 'Tente novamente em instantes.',
        variant: 'error',
        soft: true,
      })
    } finally {
      setDeleting(false)
      setConfirmDeleteOpen(false)
      setCurrent(null)
    }
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
          {error}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-sm font-medium">Interessados</h2>
          <p className="text-xs text-foreground/60">
            Pessoas que enviaram interesse pelo formulário do programa de afiliados.
          </p>
        </div>
        <div className="w-full max-w-xs">
          <Input
            value={search}
            onChange={(e) => {
              setPage(1)
              setSearch(e.target.value)
            }}
            placeholder="Buscar por nome, e-mail, WhatsApp ou mídia social"
          />
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <AffiliateLeadsTable
          items={items}
          onViewNotes={handleViewNotes}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
        />
      )}

      {totalPages > 1 && (
        <div className="pt-2">
          <Pagination page={page} total={totalPages} onChange={setPage} />
        </div>
      )}

      <SimpleFeedbackModal
        open={notesOpen && !!current}
        onClose={() => setNotesOpen(false)}
        title={current?.full_name || 'Observações'}
        description={current?.notes || 'Nenhuma observação cadastrada para este interessado.'}
        primaryLabel="Fechar"
      />

      <AffiliateLeadEditModal
        open={editOpen}
        lead={current}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />

      <AlertModal
        open={confirmDeleteOpen}
        onClose={() => !deleting && setConfirmDeleteOpen(false)}
        title="Excluir interessado"
        variant="warning"
        description={
          <div className="space-y-2 text-sm">
            <p>
              Esta ação irá remover permanentemente o interessado{' '}
              <strong>{current?.full_name || ''}</strong> da lista.
            </p>
            <p className="text-foreground/70">
              Use esta opção apenas para leads de teste ou registros inseridos por engano.
            </p>
          </div>
        }
        confirmText={deleting ? 'Excluindo...' : 'Confirmar exclusão'}
        onConfirm={handleConfirmDelete}
      />
    </div>
  )
}
